/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Responsive Collapsible Sidebar Component (Task 10.1.2 & 10.7.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React from 'react';
import { useUiStore } from '../../stores/useUiStore';
import { useTheme } from '../../context/ThemeProvider';
import { useI18n } from '../../stores/useI18nStore';
import { NAVIGATION_SECTIONS, type NavigationItem } from '../../navigation/navigation-items';
import { WalletBadge } from '../wallet/WalletBadge';
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { LOGO_VARIANTS } from '@sofia/assets';
import { SophiaIcon } from '@sofia/assets';

export interface SidebarProps {
  currentPath: string;
  onNavigate: (path: string) => void;
  expanded?: boolean;
}

export const Sidebar: React.FC<SidebarProps> = ({ currentPath, onNavigate, expanded: expandedProp }) => {
  const uiSidebarExpanded = useUiStore((state) => state.sidebarExpanded);
  const toggleSidebar = useUiStore((state) => state.toggleSidebar);
  const { themeData, isDark } = useTheme();

  const sidebarExpanded = typeof expandedProp === 'boolean' ? expandedProp : uiSidebarExpanded;
  const { t } = useI18n();

  const branding = themeData?.branding;
  const siteName = branding?.siteName || 'Sophia Platform';

  const fullLogoUrl =
    (isDark ? branding?.logoDarkUrl : branding?.logoLightUrl) ||
    (isDark ? LOGO_VARIANTS.fullDark : LOGO_VARIANTS.fullLight);
  const collapsedLogoUrl = branding?.logoCollapsedUrl || LOGO_VARIANTS.mark;

  return (
    <aside
      className={`relative hidden md:flex flex-col border-r border-surface-border bg-surface select-none transition-all duration-300 ease-in-out z-30 ${
        sidebarExpanded ? 'w-[260px]' : 'w-[68px]'
      }`}
      aria-label="Main Navigation Sidebar"
    >
      {/* 1. Sidebar Brand Header */}
      <div className="h-16 flex items-center px-4 border-b border-surface-border justify-between overflow-hidden">
        {sidebarExpanded ? (
          <button
            type="button"
            onClick={() => onNavigate('/workspace/dashboard')}
            className="flex items-center gap-3 overflow-hidden text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg transition-transform hover:opacity-90"
            title={siteName}
          >
            <img
              src={fullLogoUrl}
              alt={siteName}
              className="h-8 max-w-[170px] object-contain"
              onError={(e) => {
                (e.currentTarget as HTMLElement).style.display = 'none';
                const fb = e.currentTarget.nextElementSibling as HTMLElement;
                if (fb) fb.style.display = 'flex';
              }}
            />
            <div style={{ display: 'none' }} className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-primary flex-shrink-0 flex items-center justify-center text-primary-fg shadow-glow-primary">
                <SophiaIcon name="hologram" size={16} className="text-primary-fg" />
              </div>
              <div className="flex flex-col truncate">
                <span className="font-heading font-extrabold text-base text-foreground tracking-tight truncate">
                  {siteName}
                </span>
                <span className="text-[10px] text-foreground-muted font-mono tracking-wider uppercase">
                  Edge Studio
                </span>
              </div>
            </div>
          </button>
        ) : (
          <button
            type="button"
            onClick={() => onNavigate('/workspace/dashboard')}
            className="w-full flex justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg transition-transform hover:scale-105"
            title={siteName}
          >
            <img
              src={collapsedLogoUrl}
              alt={siteName}
              className="w-8 h-8 object-contain"
              onError={(e) => {
                (e.currentTarget as HTMLElement).style.display = 'none';
                const fb = e.currentTarget.nextElementSibling as HTMLElement;
                if (fb) fb.style.display = 'flex';
              }}
            />
            <div
              style={{ display: 'none' }}
              className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center text-primary-fg shadow-glow-primary"
              title={siteName}
            >
              <SophiaIcon name="hologram" size={16} className="text-primary-fg" />
            </div>
          </button>
        )}
      </div>

      {/* 2. Scrollable Section & Route Items */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden py-4 px-2 space-y-6">
        {NAVIGATION_SECTIONS.map((section) => {
          const sectionTitle = t(
            `nav.sections.${section.id}` as any,
            undefined,
            section.title,
          );

          return (
            <div key={section.id} className="space-y-1">
              {sidebarExpanded ? (
                <div className="flex items-center gap-2 px-3 py-1">
                  {section.sophiaIcon && (
                    <SophiaIcon
                      name={section.sophiaIcon}
                      size={12}
                      className="text-foreground-muted/60 shrink-0"
                    />
                  )}
                  <h4 className="text-[11px] font-semibold text-foreground-muted/70 tracking-wider uppercase truncate">
                    {sectionTitle}
                  </h4>
                </div>
              ) : (
                <div className="my-2 border-t border-surface-border/50 mx-2" />
              )}

              <div className="space-y-0.5">
                {section.items.map((item) => {
                  const isActive = currentPath === item.path;
                  return (
                    <SidebarNavItem
                      key={item.id}
                      item={item}
                      isActive={isActive}
                      expanded={sidebarExpanded}
                      onNavigate={onNavigate}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* 2.5 Real-Time Credit Quota Widget (Task 10.1.5) */}
      <div className="px-3 py-2 border-t border-surface-border/60">
        <WalletBadge variant="sidebar" collapsed={!sidebarExpanded} />
      </div>

      {/* 3. Bottom Collapse Toggle Strip */}
      <div className="p-3 border-t border-surface-border flex items-center justify-between">
        <button
          onClick={toggleSidebar}
          className={`w-full flex items-center gap-2 p-2 rounded-lg text-xs font-medium text-foreground-muted hover:text-foreground hover:bg-muted/60 transition-colors ${
            !sidebarExpanded ? 'justify-center' : ''
          }`}
          title={sidebarExpanded ? t('nav.collapseSidebar') : t('nav.expandSidebar')}
          aria-label={sidebarExpanded ? t('nav.collapseSidebar') : t('nav.expandSidebar')}
        >
          {sidebarExpanded ? (
            <>
              <ChevronLeft className="w-4 h-4" />
              <span>{t('nav.collapseSidebar')}</span>
            </>
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </button>
      </div>
    </aside>
  );
};

interface SidebarNavItemProps {
  item: NavigationItem;
  isActive: boolean;
  expanded: boolean;
  onNavigate: (path: string) => void;
}

const SidebarNavItem: React.FC<SidebarNavItemProps> = ({ item, isActive, expanded, onNavigate }) => {
  const { t } = useI18n();
  const Icon = item.icon;

  const itemLabel = t(
    `nav.items.${item.id}.label` as any,
    undefined,
    item.label,
  );

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (item.isExternal) {
      window.open(item.path, '_blank', 'noopener,noreferrer');
    } else {
      onNavigate(item.path);
    }
  };

  return (
    <div className="relative group">
      <button
        onClick={handleClick}
        className={`w-full flex items-center rounded-lg text-xs font-medium transition-all duration-150 ${
          expanded ? 'px-3 py-2 gap-3' : 'justify-center p-2.5'
        } ${
          isActive
            ? 'bg-primary/10 text-primary font-semibold shadow-sm'
            : 'text-foreground-muted hover:text-foreground hover:bg-muted/50'
        }`}
        aria-current={isActive ? 'page' : undefined}
      >
        {item.sophiaIcon ? (
          <SophiaIcon
            name={item.sophiaIcon}
            size={16}
            className={`w-4 h-4 flex-shrink-0 transition-colors ${
              isActive ? 'text-primary' : 'text-foreground-muted group-hover:text-foreground'
            }`}
          />
        ) : (
          <Icon
            className={`w-4 h-4 flex-shrink-0 transition-colors ${
              isActive ? 'text-primary' : 'text-foreground-muted group-hover:text-foreground'
            }`}
          />
        )}

        {expanded && (
          <div className="flex-1 flex items-center justify-between truncate">
            <span className="truncate">{itemLabel}</span>
            <div className="flex items-center gap-1.5 ml-2">
              {item.badge && (
                <span
                  className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wider ${
                    item.badgeVariant === 'accent'
                      ? 'bg-accent/15 text-accent border border-accent/20'
                      : item.badgeVariant === 'emerald'
                      ? 'bg-emerald-500/15 text-emerald-500 border border-emerald-500/20'
                      : 'bg-primary/15 text-primary border border-primary/20'
                  }`}
                >
                  {item.badge}
                </span>
              )}
              {item.isExternal && <ExternalLink className="w-3 h-3 text-foreground-muted" />}
            </div>
          </div>
        )}
      </button>

      {/* Floating Tooltip in Collapsed Mode */}
      {!expanded && (
        <div
          role="tooltip"
          className="pointer-events-none fixed left-[76px] z-50 hidden group-hover:flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-surface-elevated text-foreground text-xs shadow-glass border border-surface-border whitespace-nowrap animate-fade-in"
          style={{ transform: 'translateY(-50%)' }}
        >
          {item.sophiaIcon && (
            <SophiaIcon name={item.sophiaIcon} size={14} className="text-primary shrink-0" />
          )}
          <span className="font-medium">{itemLabel}</span>
          {item.badge && (
            <span
              className={`text-[9px] font-bold px-1 py-0.5 rounded-full uppercase ${
                item.badgeVariant === 'accent'
                  ? 'bg-accent/20 text-accent'
                  : item.badgeVariant === 'emerald'
                  ? 'bg-emerald-500/20 text-emerald-500'
                  : 'bg-primary/20 text-primary'
              }`}
            >
              {item.badge}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
